import styled from '@emotion/styled'
import { FlexDirectionProperty } from 'csstype'
import { textMixin } from '../../mixins/TextMixin/TextMixin'
import { WebLink } from '@news-mono/web-common'
import {
    calcRem,
    getComponentTheme,
    breakpointSwitch,
    Theme,
    TheNightlyThemeConfig,
} from '../../__styling'

const getHeaderStyles = (
    theme: Theme,
    flexDirection: FlexDirectionProperty = 'row',
) => {
    const articleCardsHeaderTheme = getComponentTheme(
        theme,
        'articleCards',
    ).header

    return {
        display: 'flex' as const,
        gap: calcRem(theme.margins.md),
        alignItems: flexDirection === 'row' ? 'center' : 'flex-start',
        flexDirection,
        ...breakpointSwitch(
            articleCardsHeaderTheme.breakpointState,
            ({ gap, marginTop }) => ({
                gap: flexDirection === 'row' ? gap : theme.margins.xs,
                marginTop,
            }),
        ),
    }
}

export const StyledArticlesContainer = styled.div(({ theme }) => {
    const articleCardsContainerTheme = getComponentTheme(
        theme,
        'articleCards',
    ).container

    return [
        {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
        },
        breakpointSwitch(
            articleCardsContainerTheme.breakpointState,
            ({ maxWidth, margin, gap }) => ({
                maxWidth,
                margin,
                gap,
            }),
        ),
    ]
})

export const StyledArticlesHeader = styled.div(({ theme }) =>
    getHeaderStyles(theme),
)

export const StyledArticlesHeaderLink = styled(WebLink)(({ theme }) => ({
    ...getHeaderStyles(theme, 'column'),
    color: 'inherit',
    textDecoration: 'none',
    width: 'fit-content',
    flexDirection: 'column',
}))

export const StyledReadViewAll = styled.div(({ theme }) => {
    const readMoreTheme = getComponentTheme(theme, 'sectionHeader').readMore
    return {
        display: 'flex',
        alignItems: 'center',
        gap: readMoreTheme?.gap,
    }
})

export const StyledViewAllText = styled.span(({ theme }) => {
    const readMoreTheme = getComponentTheme(theme, 'sectionHeader').readMore
    return [
        {
            color: readMoreTheme?.textColor,
        },
        readMoreTheme?.textType && textMixin(theme, readMoreTheme.textType),
    ]
})

export const StyledArticlesHeaderIcon = styled.div(({ theme }) => {
    const articleCardsHeaderIcon = getComponentTheme(theme, 'articleCards').svg

    return [
        breakpointSwitch(
            articleCardsHeaderIcon.breakpointState,
            ({ height, width }) => ({
                height,
                width,
            }),
        ),
    ]
})

export const StyledArticlesHeaderText = styled.div<{
    useSectionColor?: boolean
}>(({ theme, useSectionColor }) => {
    const articleCardsHeaderTextTheme = getComponentTheme(
        theme,
        'articleCards',
    ).headerText

    return [
        {
            fontWeight: 700,
            flexGrow: 1,
            margin: 0,
            fontFamily: textMixin<TheNightlyThemeConfig>(
                theme,
                'section-header',
            ).fontFamily,
        },
        breakpointSwitch(
            articleCardsHeaderTextTheme.breakpointState,
            ({ fontSize, letterSpacing, lineHeight, color }) => ({
                fontSize,
                letterSpacing,
                lineHeight,
                color: useSectionColor ? color : undefined,
            }),
        ),
    ]
})

export const StyledArticlesGrid = styled.div(({ theme }) => {
    const articleCardsGridContainerTheme = getComponentTheme(
        theme,
        'articleCards',
    ).gridContainer

    return [
        {
            display: 'grid',
            gridTemplateRows: 'auto',
        },
        breakpointSwitch(
            articleCardsGridContainerTheme.breakpointState,
            ({ gridTemplateColumns, columnGap }) => ({
                gridTemplateColumns,
                columnGap,
            }),
        ),
    ]
})

export const StyledArticlesArticle = styled.div(({ theme }) => {
    const articleCardsTheme = getComponentTheme(theme, 'articleCards')

    return {
        borderBottom: articleCardsTheme.border,
        padding: calcRem(theme.margins.md, 0),
    }
})
